import { useContext, useEffect, useMemo, useState } from 'react';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';

export const PWA_INSTALL_SNOOZE_KEY = 'jg:pwaInstallSnoozedUntil:v1';
export const LEGACY_PWA_INSTALL_SNOOZE_KEY = 'snoozeTimestamp';
const PATCH_DONE_PREFIX = 'jg:pwaInstallCreditPatched:v1';
const PATCH_ATTEMPT_PREFIX = 'jg:pwaInstallCreditPatchAttemptAt:v1';
const PATCH_ATTEMPT_THROTTLE_MS = 1000 * 60 * 60 * 6;
const SNOOZE_MS = 1000 * 60 * 60 * 24;

const listeners = new Set();
let initialized = false;
let patchInFlight = false;

const getNow = () => Date.now();

const canUseWindow = () => typeof window !== 'undefined';

const safeGetStorage = (key) => {
  if (!canUseWindow()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetStorage = (key, value) => {
  if (!canUseWindow()) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {}
};

const safeRemoveStorage = (key) => {
  if (!canUseWindow()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {}
};

const getSnoozedUntil = () => {
  const current = Number(safeGetStorage(PWA_INSTALL_SNOOZE_KEY));
  const legacy = Number(safeGetStorage(LEGACY_PWA_INSTALL_SNOOZE_KEY));
  return Math.max(
    Number.isFinite(current) ? current : 0,
    Number.isFinite(legacy) ? legacy : 0,
  );
};

const getIsStandalone = () => {
  if (!canUseWindow()) return false;
  const standaloneMedia = window.matchMedia?.('(display-mode: standalone)')
    ?.matches;
  return Boolean(standaloneMedia || window.navigator?.standalone === true);
};

const getUserAgent = () =>
  canUseWindow() ? String(window.navigator?.userAgent || '') : '';

const getPlatform = () => {
  const ua = getUserAgent();
  const isIos = /iPad|iPhone|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isIosChrome = isIos && /CriOS/i.test(ua);
  const isIosFirefox = isIos && /FxiOS/i.test(ua);
  const isIosEdge = isIos && /EdgiOS/i.test(ua);
  const isIosSafari =
    isIos && /Safari/i.test(ua) && !isIosChrome && !isIosFirefox && !isIosEdge;
  const isAndroidFirefox = isAndroid && /Firefox/i.test(ua);
  const isAndroidChrome =
    isAndroid && /Chrome/i.test(ua) && !/Firefox|EdgA|OPR/i.test(ua);

  if (isIosSafari) return 'ios-safari';
  if (isIosChrome || isIosFirefox || isIosEdge) return 'ios-non-safari';
  if (isAndroidFirefox) return 'android-firefox';
  if (isAndroidChrome) return 'android-chrome';
  if (isAndroid) return 'android-other';
  return 'other';
};

const state = {
  deferredPrompt: null,
  hasInstalledEvent: false,
  isStandalone: getIsStandalone(),
  nativePromptConsumed: false,
  nativePromptDismissed: false,
  sheetOpen: false,
  sheetOwner: null,
  snoozeOverrideThisSession: false,
};

const notify = () => {
  listeners.forEach((listener) => listener());
};

const getSnapshot = () => {
  const snoozedUntil = getSnoozedUntil();
  const isSnoozed =
    !state.snoozeOverrideThisSession && snoozedUntil > getNow();
  const platform = getPlatform();
  const isStandalone = state.isStandalone || getIsStandalone();
  const shouldShowInstallUi = !isStandalone && !state.hasInstalledEvent;
  const canNativeInstall =
    Boolean(state.deferredPrompt) && !state.nativePromptConsumed;
  return {
    ...state,
    canNativeInstall,
    isSnoozed,
    isStandalone,
    platform,
    shouldShowInstallUi,
    showCompactPrompt: shouldShowInstallUi && isSnoozed,
    showFullPrompt: shouldShowInstallUi && !isSnoozed,
    snoozedUntil,
  };
};

const setState = (updates) => {
  Object.assign(state, updates);
  notify();
};

const recalculateStandalone = () => {
  const next = getIsStandalone();
  if (next !== state.isStandalone) {
    setState({ isStandalone: next });
  }
};

const initStore = () => {
  if (initialized || !canUseWindow()) return;
  initialized = true;
  recalculateStandalone();

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    setState({
      deferredPrompt: event,
      nativePromptConsumed: false,
      nativePromptDismissed: false,
    });
  });

  window.addEventListener('appinstalled', () => {
    setState({
      deferredPrompt: null,
      hasInstalledEvent: true,
      nativePromptConsumed: true,
      sheetOpen: false,
      sheetOwner: null,
    });
  });

  window.addEventListener('focus', recalculateStandalone);
  document.addEventListener?.('visibilitychange', recalculateStandalone);

  const media = window.matchMedia?.('(display-mode: standalone)');
  if (media?.addEventListener) {
    media.addEventListener('change', recalculateStandalone);
  } else if (media?.addListener) {
    media.addListener(recalculateStandalone);
  }
};

const getPlayerId = (player) =>
  player?.data?.player?._id || player?.data?.player?.playerId || 'current';

const getPatchDoneKey = (player) => `${PATCH_DONE_PREFIX}:${getPlayerId(player)}`;

const getPatchAttemptKey = (player) =>
  `${PATCH_ATTEMPT_PREFIX}:${getPlayerId(player)}`;

const shouldAttemptPatch = (player) => {
  if (!player?.token) return false;
  if (player?.data?.player?.pwaIsInstalled === true) {
    safeSetStorage(getPatchDoneKey(player), '1');
    return false;
  }
  if (safeGetStorage(getPatchDoneKey(player)) === '1') return false;
  const lastAttempt = Number(safeGetStorage(getPatchAttemptKey(player)));
  if (
    Number.isFinite(lastAttempt) &&
    getNow() - lastAttempt < PATCH_ATTEMPT_THROTTLE_MS
  ) {
    return false;
  }
  return true;
};

const patchInstallCredit = async (player) => {
  if (patchInFlight || !shouldAttemptPatch(player)) return;
  patchInFlight = true;
  safeSetStorage(getPatchAttemptKey(player), String(getNow()));
  try {
    await axiosConn.patch(
      'players/updateMe',
      { pwaIsInstalled: true },
      {
        headers: {
          Authorization: `Bearer ${player.token}`,
        },
      },
    );
    safeSetStorage(getPatchDoneKey(player), '1');
  } catch {
    // Best effort only. A later standalone authenticated launch can retry.
  } finally {
    patchInFlight = false;
  }
};

export const pwaInstallRecoveryActions = {
  openHelpSheet(owner = 'global') {
    setState({
      sheetOpen: true,
      sheetOwner: owner,
      snoozeOverrideThisSession: true,
    });
  },
  closeHelpSheet() {
    setState({ sheetOpen: false, sheetOwner: null });
  },
  snoozeFullPrompt() {
    const until = getNow() + SNOOZE_MS;
    safeSetStorage(PWA_INSTALL_SNOOZE_KEY, String(until));
    setState({ snoozeOverrideThisSession: false });
  },
  clearSnoozeForSession() {
    setState({ snoozeOverrideThisSession: true });
  },
  async promptNativeInstall() {
    const promptEvent = state.deferredPrompt;
    if (!promptEvent || state.nativePromptConsumed) {
      setState({
        nativePromptDismissed: true,
        nativePromptConsumed: true,
        deferredPrompt: null,
      });
      return { outcome: 'unavailable' };
    }
    setState({ nativePromptConsumed: true });
    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      if (choice?.outcome === 'accepted') {
        setState({
          deferredPrompt: null,
          nativePromptDismissed: false,
          sheetOpen: false,
          sheetOwner: null,
        });
      } else {
        setState({
          deferredPrompt: null,
          nativePromptDismissed: true,
        });
      }
      return choice || { outcome: 'dismissed' };
    } catch {
      setState({
        deferredPrompt: null,
        nativePromptDismissed: true,
      });
      return { outcome: 'dismissed' };
    }
  },
  resetForTests() {
    Object.assign(state, {
      deferredPrompt: null,
      hasInstalledEvent: false,
      isStandalone: getIsStandalone(),
      nativePromptConsumed: false,
      nativePromptDismissed: false,
      sheetOpen: false,
      sheetOwner: null,
      snoozeOverrideThisSession: false,
    });
    safeRemoveStorage(PWA_INSTALL_SNOOZE_KEY);
    safeRemoveStorage(LEGACY_PWA_INSTALL_SNOOZE_KEY);
    notify();
  },
};

export const getPwaInstallRecoverySnapshot = getSnapshot;

export default function usePwaInstallRecovery(owner = 'global') {
  const { player } = useContext(AuthContext);
  const [snapshot, setSnapshot] = useState(() => getSnapshot());

  useEffect(() => {
    initStore();
    const listener = () => setSnapshot(getSnapshot());
    listeners.add(listener);
    listener();
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (snapshot.isStandalone || snapshot.hasInstalledEvent) {
      patchInstallCredit(player);
    }
  }, [player, snapshot.hasInstalledEvent, snapshot.isStandalone]);

  return useMemo(
    () => ({
      ...snapshot,
      isSheetOpenForOwner:
        snapshot.sheetOpen && (!snapshot.sheetOwner || snapshot.sheetOwner === owner),
      closeHelpSheet: pwaInstallRecoveryActions.closeHelpSheet,
      openHelpSheet: () => pwaInstallRecoveryActions.openHelpSheet(owner),
      promptNativeInstall: pwaInstallRecoveryActions.promptNativeInstall,
      snoozeFullPrompt: pwaInstallRecoveryActions.snoozeFullPrompt,
    }),
    [owner, snapshot],
  );
}
